import { AxiosError } from 'axios';
import { useIntl } from 'react-intl';

import { uploadAtttachments } from '~/api/tickets';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { useAppConfigContext } from '~/contexts/AppConfig';
import { FileInput } from '~/eds/src/components/FileInput/FileInput';
import { Filename } from '~/eds/src/components/Filename/Filename';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { IntakeFormFile } from '~/types/IntakeFormFile';
import { Nullable } from '~/types/Nullable';
import * as ToastTypes from '~/types/toast.types';
import { fileTypesToMimeTypeGrouping } from '~/utils/fileTypesToMimeTypeGrouping';

import { FileCustomSettings } from '../types/fields';
import { downloadTicketFileAttachment } from '../utils/downloadTicketFileAttachment';
import { getFullIntakeFormFileName } from '../utils/getFullIntakeFormFileName';
import { WorkflowQuestionFormField } from './types';

export const FileWorkflowQuestion = ({
  question,
  onChange,
  disabled,
  label,
  ...rest
}: WorkflowQuestionFormField<Nullable<File[]>>) => {
  const { isPanelEnlarged } = useAppConfigContext();
  const intl = useIntl();
  const validateAttachments = useFlag(FlagType.CheckFormFieldFileExtension);

  const {
    field: { id: fieldId, value, customSettings },
  } = question;
  const {
    allowed_file_type_rule: allowedFileTypeRule,
    allowed_file_types: userSpecifiedFileTypes,
    file_limit: fileLimit,
  } = customSettings as FileCustomSettings;

  const allFileTypes = Object.values(FileExtensionType);
  const anyExceptFileTypes = allFileTypes.filter(
    (type) => !userSpecifiedFileTypes?.includes(type),
  );

  let acceptedFileTypes: FileExtensionType[] | '*';
  if (allowedFileTypeRule === 'any_except') {
    acceptedFileTypes = anyExceptFileTypes;
  } else if (userSpecifiedFileTypes) {
    acceptedFileTypes = userSpecifiedFileTypes as FileExtensionType[];
  } else {
    acceptedFileTypes = '*';
  }

  const handleFileDataChange = (newFiles: IntakeFormFile[]) => {
    const newValue = [...(value.value || []), ...newFiles];
    onChange(newValue.length ? newValue : null, 'value');
  };

  const handleRemoveFile = (file: IntakeFormFile) => {
    const newFiles = value.value.filter(
      (f: IntakeFormFile) => f.id !== file.id,
    );

    onChange(newFiles.length ? newFiles : null, 'value');
  };

  const handleFileAttachError = (
    error: AxiosError<{ data: string }>,
    fileName: string,
  ) => {
    let message = intl.formatMessage({
      id: 'Files.attachFileFailed',
      defaultMessage: 'Attach file failed. Please try again.',
      description:
        'Error message when uploading an attachment to the intake form fails.',
    });

    if (validateAttachments && error?.response?.status === 400) {
      message = intl.formatMessage(
        {
          id: 'Files.attachFileFailed2',
          defaultMessage: '{fileName} failed to upload. {error}',
          description:
            'Error message when uploading an attachment to the intake form fails. Example: fileName = "document.pdf", error = "Invalid format"',
        },
        {
          fileName,
          error: error.response.data.data,
        },
      );
    }
    showToast(ToastTypes.ERROR, message);
  };

  return (
    <>
      <FormField
        {...rest}
        label={label}
        input={FileInput}
        value={value.value}
        disabled={disabled || (value?.value?.length && fileLimit === 'single')}
        onChange={(files) => {
          uploadAtttachments(
            files,
            handleFileDataChange,
            handleFileAttachError,
            validateAttachments ? fieldId : undefined,
          );
        }}
        inputProps={{
          isMulti: fileLimit !== 'single',
          accept:
            acceptedFileTypes !== '*'
              ? fileTypesToMimeTypeGrouping(
                  acceptedFileTypes as FileExtensionType[],
                )
              : undefined,
          width: isPanelEnlarged ? 'input.m.width' : '100%',
        }}
      />
      <Layout
        direction="column"
        spacing={1}
        align="start"
        as="ul"
        aria-label={intl.formatMessage(
          {
            id: 'FileWorkflowQuestion.attachedFiles',
            defaultMessage: '{label} files',
            description:
              'Accessible label for the list of attached files in a workflow intake form. Example: label = "Contract Documents"',
          },
          { label },
        )}
        p={0}
        m={0}
        listStyleType="none"
      >
        {value.value?.map((file: IntakeFormFile) => {
          const fileName = getFullIntakeFormFileName(file);

          return (
            <Layout
              key={file.id}
              as="li"
              direction="row"
              align="center"
              spacing={1}
              maxW="100%"
            >
              <Layout flex="1 1 0" minW={0} overflowX="hidden">
                <Filename
                  key={file.id}
                  name={fileName}
                  onClick={() => downloadTicketFileAttachment(file, intl)}
                />
              </Layout>
              <Layout flex="0 0 auto">
                <IconButton
                  icon="x"
                  size="xs"
                  disabled={disabled}
                  onClick={() => handleRemoveFile(file)}
                  tooltip={intl.formatMessage(
                    {
                      id: 'FileWorkflowQuestion.removeFile',
                      defaultMessage: 'Remove {fileName}',
                      description:
                        'Tooltip for removing a file from intake form attachements question. Example: fileName = "document.pdf"',
                    },
                    {
                      fileName,
                    },
                  )}
                />
              </Layout>
            </Layout>
          );
        })}
      </Layout>
    </>
  );
};
